/*
 * NC Bundle Picker — modal de selección de pieza reutilizable (landing + PDP), flujo SECUENCIAL.
 * Sin lógica de carrito: entrega (productId, variantId, slot) mediante `onPick`. El add-to-cart y el descuento 3×2
 * siguen en nc-bundle + el descuento automático de Shopify.
 *
 * API: picker.open({ slot, total: 3, trigger,
 *                    sequence: [slots a llenar en orden]   // p. ej. [0,1,2] (landing) o [1,2] (PDP); [i] = cambiar una pieza
 *                    mode: 'add' | 'change',
 *                    getState(): { selected: [info|null ×3] }   // estado FRESCO tras cada elección
 *                    onPick(productId, variantId, slot), onStep(slot), onClose({ completed }) })
 *      info = { productId, variantId, variantTitle }
 *
 * Máquina de estados:
 *   - Al elegir una pieza (Agregar al set) se aplica de inmediato (onPick) y el MISMO modal avanza a la siguiente
 *     pieza de la secuencia sin cerrarse. Tras la última, se cierra (completed = true).
 *   - ATRÁS vuelve a la pieza anterior de la secuencia con su selección cargada.
 *   - X / Escape / fondo cierran conservando lo ya elegido (cada elección ya está aplicada en el builder).
 *   - mode 'change' = una sola pieza ("Cambiar pieza N"): se elige y se cierra.
 *
 * Estado visual "ELEGIDA": la tarjeta de la pieza que ocupa el SLOT ACTUAL lleva .is-chosen (contorno de tinta + check),
 * `aria-current="true"` y la etiqueta visible "✓ Elegida · Plata" (no depende del color). Es solo un estado del slot: no
 * deshabilita nada, así que se pueden repetir piezas (la promoción lo permite). Las piezas de OTROS slots se indican como
 * "En tu set · pieza N · Oro".
 */
const norm = (value) => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

class NCBundlePicker extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    // El <dialog> vive en <body>: su cadena de scroll no pasa por .page-wrapper (scroller real del theme en desktop).
    if (this.parentElement !== document.body) {
      document.querySelectorAll('nc-bundle-picker').forEach((el) => {
        if (el !== this && el.parentElement === document.body) el.remove();
      });
      document.body.appendChild(this);
      return;
    }
    this.ready = true;
    this.dialog = this.querySelector('[data-ncbp-dialog]');
    this.body = this.querySelector('[data-ncbp-body]');
    this.searchInput = this.querySelector('[data-ncbp-search]');
    this.statusEl = this.querySelector('[data-ncbp-status]');
    this.emptyEl = this.querySelector('[data-ncbp-empty]');
    this.backBtn = this.querySelector('[data-ncbp-back]');
    this.progressEl = this.querySelector('[data-ncbp-progress]');
    this.panel = this.querySelector('.ncbp__panel');
    this.cards = [...this.querySelectorAll('[data-ncbp-card]')];
    this.cats = [...this.querySelectorAll('[data-ncbp-cat]')];
    this.catalog = JSON.parse(this.querySelector('[data-ncbp-catalog]').textContent || '{}');
    this.category = '';
    this.isOpen = false;
    this.closeTimer = 0;
    this.ownClose = 0;
    this.seq = [0];
    this.seqIndex = 0;
    this.completed = false;

    this.querySelectorAll('[data-ncbp-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());
    // Click en el fondo (fuera del panel) cierra.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.trap(event);
    });
    // El evento nativo `close` es asíncrono: los cierres iniciados aquí se cuentan y se ignoran para que un evento
    // tardío no cierre una apertura posterior. Solo un cierre nativo inesperado sincroniza el estado.
    this.dialog.addEventListener('close', () => {
      if (this.ownClose > 0) {
        this.ownClose -= 1;
        return;
      }
      if (this.isOpen) this.finish();
    });

    this.searchInput.addEventListener('input', () => this.applyFilters());
    this.cats.forEach((btn) =>
      btn.addEventListener('click', () => {
        this.category = btn.dataset.ncbpCat;
        this.cats.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.applyFilters();
      })
    );

    this.cards.forEach((card) => {
      card._sel = [];
      card._img = card.querySelector('.ncbp__img');
      card._img0 = card._img ? { src: card._img.getAttribute('src'), srcset: card._img.getAttribute('srcset') } : null;
      card.querySelectorAll('[data-ncbp-option]').forEach((chip) =>
        chip.addEventListener('click', () => {
          const index = Number(chip.dataset.ncbpOption);
          card._sel[index] = card._sel[index] === chip.dataset.value ? null : chip.dataset.value;
          this.refreshCard(card);
        })
      );
      card.querySelector('[data-ncbp-add]').addEventListener('click', () => this.pick(card));
    });
  }

  productOf(card) {
    return this.catalog[card.dataset.productId];
  }

  isDefault(product) {
    return product.options.length === 1 && product.options[0] === 'Title';
  }

  variantFor(card) {
    const product = this.productOf(card);
    if (!product) return null;
    if (this.isDefault(product)) return product.variants[0];
    if (card._sel.length < product.options.length || card._sel.some((v) => v == null)) return null;
    return product.variants.find((variant) => variant.options.every((value, i) => value === card._sel[i])) || null;
  }

  refreshCard(card) {
    const product = this.productOf(card);
    const variant = this.variantFor(card);
    // Chips: pressed + disponibilidad (sin variante disponible dada la selección de las otras opciones)
    card.querySelectorAll('[data-ncbp-option]').forEach((chip) => {
      const index = Number(chip.dataset.ncbpOption);
      const value = chip.dataset.value;
      chip.setAttribute('aria-pressed', String(card._sel[index] === value));
      const ok = product.variants.some(
        (v) => v.available && v.options[index] === value && v.options.every((o, j) => j === index || card._sel[j] == null || o === card._sel[j])
      );
      chip.disabled = !ok;
    });

    const priceEl = card.querySelector('[data-ncbp-price]');
    if (variant) {
      priceEl.innerHTML = `<span data-ncbp-now>${variant.price}</span>${variant.compare ? ` <s>${variant.compare}</s>` : ''}`;
      const img = card._img;
      if (img && variant.image) {
        img.removeAttribute('srcset');
        img.src = variant.image;
      }
    } else {
      priceEl.innerHTML = `<span data-ncbp-now>${priceEl.dataset.default}</span>`;
      if (card._img && card._img0) {
        card._img.src = card._img0.src;
        if (card._img0.srcset) card._img.setAttribute('srcset', card._img0.srcset);
      }
    }

    const add = card.querySelector('[data-ncbp-add]');
    if (variant) {
      add.disabled = !variant.available;
      add.textContent = variant.available ? this.dataset.add : this.dataset.soldOut;
    } else {
      add.disabled = true;
      const missing = product.options[Math.max(0, card._sel.findIndex((v) => v == null))];
      const anyAvailable = product.variants.some((v) => v.available);
      add.textContent = anyAvailable ? this.dataset.choose.replace('[option]', String(missing).toLowerCase()) : this.dataset.soldOut;
    }
  }

  // Estado visual: "✓ Elegida · Plata" para el slot actual + "En tu set · pieza N · Oro" para los otros slots.
  markInSet(card, selected, current, slot) {
    const el = card.querySelector('[data-ncbp-inset]');
    const pid = card.dataset.productId;
    el.replaceChildren();
    let chosen = false;
    if (current && String(current.productId) === pid) {
      chosen = true;
      const tag = document.createElement('span');
      tag.className = 'ncbp__chosen-tag';
      tag.textContent = `✓ ${this.dataset.chosen || 'Elegida'} · ${current.variantTitle}`;
      el.append(tag);
    }
    selected.forEach((info, n) => {
      if (info && n !== slot && String(info.productId) === pid) {
        const other = document.createElement('span');
        other.className = 'ncbp__inset-other';
        other.textContent = `${this.dataset.inSet || 'En tu set'} · pieza ${n + 1} · ${info.variantTitle}`;
        el.append(other);
      }
    });
    el.hidden = !el.childNodes.length;
    card.classList.toggle('is-chosen', chosen);
    if (chosen) card.setAttribute('aria-current', 'true');
    else card.removeAttribute('aria-current');
  }

  open(opts) {
    if (this.isOpen) return;
    clearTimeout(this.closeTimer);
    this.closing = false;
    this.opts = opts;
    this.trigger = opts.trigger || document.activeElement;
    this.seq = Array.isArray(opts.sequence) && opts.sequence.length ? opts.sequence.slice() : [opts.slot];
    this.seqIndex = Math.max(0, this.seq.indexOf(opts.slot));
    this.mode = opts.mode || 'add';
    this.completed = false;
    this.renderStep(true);

    this.savedScroll = {
      y: window.scrollY,
      pw: (document.querySelector('.page-wrapper') || {}).scrollTop || 0,
    };
    document.documentElement.classList.add('ncbp-lock');
    this.body.scrollTop = 0;
    this.isOpen = true;
    if (!this.dialog.open) this.dialog.showModal();
    // Reflow síncrono (sin depender de requestAnimationFrame, que se pausa en pestañas ocultas) para que corra la transición.
    void this.dialog.offsetWidth;
    this.dialog.classList.add('is-open');

    this.scrollToCurrent();
  }

  // Pinta el paso actual de la secuencia: título, "Pieza n de 3", progreso, Atrás y las tarjetas con la selección vigente.
  renderStep(initial) {
    const opts = this.opts;
    const slot = this.seq[this.seqIndex];
    const total = opts.total || 3;
    const state = typeof opts.getState === 'function' ? opts.getState() : { selected: opts.selected || [] };
    const selected = state.selected || [];
    const current = selected[slot] || null;

    const title = this.mode === 'change'
      ? (this.dataset.changeTitle || 'Cambiar pieza [n]').replace('[n]', slot + 1)
      : this.dataset[`title${slot + 1}`] || this.dataset.title1;
    const titleEl = this.querySelector('[data-ncbp-title]');
    titleEl.textContent = title;
    this.querySelector('[data-ncbp-step]').textContent = this.dataset.step.replace('[n]', slot + 1).replace('[total]', total);
    if (this.backBtn) this.backBtn.hidden = this.seqIndex === 0;
    if (this.progressEl) {
      this.progressEl.hidden = this.mode === 'change';
      [...this.progressEl.children].forEach((seg, n) => {
        seg.classList.toggle('is-done', Boolean(selected[n]) && n !== slot);
        seg.classList.toggle('is-current', n === slot);
      });
    }
    if (typeof opts.onStep === 'function') opts.onStep(slot);

    this.searchInput.value = '';
    this.category = '';
    this.cats.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ncbpCat === '')));

    this.cards.forEach((card) => {
      const product = this.productOf(card);
      // Con opciones (p. ej. Oro/Plata) NUNCA se elige una variante por el cliente; solo se precarga la pieza que se está editando.
      card._sel = this.isDefault(product) ? [] : product.options.map(() => null);
      if (current && String(current.productId) === card.dataset.productId) {
        const variant = product.variants.find((v) => String(v.id) === String(current.variantId));
        if (variant) card._sel = [...variant.options];
      }
      this.markInSet(card, selected, current, slot);
      this.refreshCard(card);
    });
    this.applyFilters();
    this.body.scrollTop = 0;
    if (!initial) {
      // Avance/retroceso dentro del mismo modal: microtransición (fade + 8px) del título y la cuadrícula; el título nuevo
      // se anuncia y recibe el foco. Con reduced-motion las duraciones colapsan a ~0.
      if (this.panel) {
        this.panel.classList.remove('is-stepping');
        void this.panel.offsetWidth;
        this.panel.classList.add('is-stepping');
      }
      this.scrollToCurrent();
      titleEl.focus({ preventScroll: true });
    }
  }

  scrollToCurrent() {
    const slot = this.seq[this.seqIndex];
    const state = this.opts && typeof this.opts.getState === 'function' ? this.opts.getState() : { selected: [] };
    const current = (state.selected || [])[slot];
    if (!current) return;
    const card = this.cards.find((c) => c.dataset.productId === String(current.productId));
    if (card) this.body.scrollTop = Math.max(0, card.offsetTop - 120);
  }

  applyFilters() {
    const query = norm(this.searchInput.value);
    let visible = 0;
    this.cards.forEach((card) => {
      const okCat = !this.category || card.dataset.cols.includes(`|${this.category}|`);
      const okText = !query || norm(card.dataset.name).includes(query);
      card.hidden = !(okCat && okText);
      if (!card.hidden) visible += 1;
    });
    this.emptyEl.hidden = visible !== 0;
    this.statusEl.textContent = visible === 1 ? this.dataset.countOne : this.dataset.countMany.replace('[n]', visible);
  }

  pick(card) {
    const variant = this.variantFor(card);
    if (!variant || !variant.available) return;
    const slot = this.seq[this.seqIndex];
    const onPick = this.opts && this.opts.onPick;
    if (onPick) onPick(card.dataset.productId, variant.id, slot);
    if (this.seqIndex < this.seq.length - 1) {
      this.seqIndex += 1;
      this.renderStep(false);
    } else {
      this.completed = true;
      this.close();
    }
  }

  back() {
    if (!this.isOpen || this.seqIndex === 0) return;
    this.seqIndex -= 1;
    this.renderStep(false);
  }

  close() {
    if (!this.isOpen || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');
    const raw = getComputedStyle(this).getPropertyValue('--nc-motion-normal').trim() || '.42s';
    const ms = (raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000) || 420;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.finish(), ms + 20);
  }

  finish() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.closing = false;
    clearTimeout(this.closeTimer);
    this.dialog.classList.remove('is-open');
    if (this.dialog.open) {
      this.ownClose += 1;
      this.dialog.close();
    }
    document.documentElement.classList.remove('ncbp-lock');
    // Sin salto de scroll: se restaura la posición exacta previa (window en móvil / .page-wrapper en desktop).
    const pw = document.querySelector('.page-wrapper');
    if (pw && this.savedScroll && pw.scrollTop !== this.savedScroll.pw) pw.scrollTop = this.savedScroll.pw;
    if (this.savedScroll && window.scrollY !== this.savedScroll.y) window.scrollTo({ top: this.savedScroll.y, behavior: 'instant' });
    const trigger = this.trigger;
    if (trigger && trigger.isConnected && trigger.focus) trigger.focus({ preventScroll: true });
    const onClose = this.opts && this.opts.onClose;
    const completed = this.completed;
    this.opts = null;
    this.completed = false;
    if (onClose) onClose({ completed });
  }

  trap(event) {
    const nodes = [...this.dialog.querySelectorAll('button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !el.closest('[hidden]') && el.offsetParent !== null
    );
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('nc-bundle-picker')) customElements.define('nc-bundle-picker', NCBundlePicker);
